import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface PillarInputs {
  monthlyVolume: number | ''
  targetAutomationPercentage: number
  manualInterventionPercentage: number | ''
  avgTimePerTask: number | ''
  useBenchmark: boolean
}

interface PricingInputs {
  revenueBand: string
  entityCount: number
  selectedPillars: string[]
}

interface ROIResults {
  annualSavings: number
  paybackPeriod: number
  roi: number
  totalValue?: number
  implementationCost?: number
  selectedPillars?: string[]
  pillarSavingsBreakdown?: any
  totalFTEs?: number
  annualCostPerFTE?: number
}

interface SensitivityInputs {
  volumeMultiplier: number
  automationRate: number
  hourlyCost: number
  discountRate: number
}

interface StandardROIContextType {
  // Core data
  selectedPillars: string[]
  setSelectedPillars: (pillars: string[]) => void
  
  pillarInputs: Record<string, PillarInputs>
  setPillarInputs: (inputs: Record<string, PillarInputs>) => void
  
  assumptions: {
    totalFTEs: number | ''
    annualCostPerFTE: number | ''
    currency: string
    useGlobalBenchmark: boolean
  }
  setAssumptions: (assumptions: any) => void
  
  pricingInputs: PricingInputs | null
  setPricingInputs: (pricing: PricingInputs) => void
  
  roiResults: ROIResults | null
  setROIResults: (results: ROIResults) => void
  
  sensitivityInputs: SensitivityInputs
  setSensitivityInputs: (inputs: SensitivityInputs) => void
  
  // Flow state
  flowType: 'standard'
  currentStep: string
  setCurrentStep: (step: string) => void
  
  // Persistence
  saveToStorage: () => void
  loadFromStorage: () => void
  clearData: () => void
  
  // Submission
  submitToDatabase: () => Promise<string | null>
}

const StandardROIContext = createContext<StandardROIContextType | undefined>(undefined)

const STORAGE_KEY = 'standardROIContext'

export function StandardROIProvider({ children }: { children: ReactNode }) {
  const [selectedPillars, setSelectedPillars] = useState<string[]>([])
  const [pillarInputs, setPillarInputs] = useState<Record<string, PillarInputs>>({})
  const [assumptions, setAssumptions] = useState({
    totalFTEs: '' as number | '',
    annualCostPerFTE: 85000 as number | '',
    currency: 'USD',
    useGlobalBenchmark: false
  })
  const [pricingInputs, setPricingInputs] = useState<PricingInputs | null>(null)
  const [roiResults, setROIResults] = useState<ROIResults | null>(null)
  const [sensitivityInputs, setSensitivityInputs] = useState<SensitivityInputs>({
    volumeMultiplier: 1.0,
    automationRate: 70,
    hourlyCost: 45,
    discountRate: 10
  })
  const [currentStep, setCurrentStep] = useState('pillars')

  const saveToStorage = () => {
    const data = {
      selectedPillars,
      pillarInputs,
      assumptions,
      pricingInputs,
      roiResults,
      sensitivityInputs,
      currentStep,
      flowType: 'standard' as const,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const data = JSON.parse(stored)
        setSelectedPillars(data.selectedPillars || [])
        setPillarInputs(data.pillarInputs || {})
        setAssumptions(data.assumptions || {
          totalFTEs: '',
          annualCostPerFTE: 85000,
          currency: 'USD',
          useGlobalBenchmark: false
        })
        setPricingInputs(data.pricingInputs || null)
        setROIResults(data.roiResults || null)
        setSensitivityInputs(data.sensitivityInputs || {
          volumeMultiplier: 1.0,
          automationRate: 70,
          hourlyCost: 45,
          discountRate: 10
        })
        setCurrentStep(data.currentStep || 'pillars')
      }
    } catch (error) {
      console.error('Error loading Standard ROI context from storage:', error)
    }
  }

  const clearData = () => {
    setSelectedPillars([])
    setPillarInputs({})
    setAssumptions({
      totalFTEs: '',
      annualCostPerFTE: 85000,
      currency: 'USD',
      useGlobalBenchmark: false
    })
    setPricingInputs(null)
    setROIResults(null)
    setSensitivityInputs({
      volumeMultiplier: 1.0,
      automationRate: 70,
      hourlyCost: 45,
      discountRate: 10
    })
    setCurrentStep('pillars')
    localStorage.removeItem(STORAGE_KEY)
  }

  const submitToDatabase = async (): Promise<string | null> => {
    try {
      const submissionData = {
        project_id: `standard-roi-${Date.now()}`,
        user_id: 'anonymous',
        source: 'Standard ROI',
        flow_type: 'standard',
        data: {
          selectedPillars,
          pillarInputs,
          assumptions,
          pricingInputs,
          roiResults,
          sensitivityInputs,
          submittedAt: new Date().toISOString()
        },
        // Flatten key fields for database columns
        total_ftes: assumptions.totalFTEs,
        annual_cost_per_fte: assumptions.annualCostPerFTE,
        selected_pillars: selectedPillars,
        pillar_inputs: pillarInputs,
        pillar_savings_breakdown: roiResults?.pillarSavingsBreakdown,
        used_global_benchmark: assumptions.useGlobalBenchmark,
        calculation_version: 'v3-standard',
        // Pricing fields
        platform_fee_usd: pricingInputs ? '50000' : null,
        pillar_fee_total_usd: selectedPillars.length * 25000,
        entity_fee_total_usd: (pricingInputs?.entityCount || 1) * 5000,
        annual_subscription_usd: 50000 + (selectedPillars.length * 25000) + ((pricingInputs?.entityCount || 1) * 5000),
        implementation_fee_usd: roiResults?.implementationCost || 150000,
        total_year1_investment_usd: (roiResults?.implementationCost || 150000) + 50000 + (selectedPillars.length * 25000),
        custom_investment: false
      }

      const response = await fetch('/api/roi/submit-standard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      })

      if (response.ok) {
        const result = await response.json()
        return result.submissionId
      } else {
        throw new Error('Failed to submit ROI data')
      }
    } catch (error) {
      console.error('Error submitting Standard ROI to database:', error)
      return null
    }
  }

  // Load from localStorage on mount (once)
  useEffect(() => {
    loadFromStorage()
  }, [])

  // Auto-save to localStorage when data changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveToStorage()
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [selectedPillars, pillarInputs, assumptions, pricingInputs, roiResults, sensitivityInputs, currentStep])

  const value: StandardROIContextType = {
    selectedPillars,
    setSelectedPillars,
    pillarInputs,
    setPillarInputs,
    assumptions,
    setAssumptions,
    pricingInputs,
    setPricingInputs,
    roiResults,
    setROIResults,
    sensitivityInputs,
    setSensitivityInputs,
    flowType: 'standard',
    currentStep,
    setCurrentStep,
    saveToStorage,
    loadFromStorage,
    clearData,
    submitToDatabase
  }

  return (
    <StandardROIContext.Provider value={value}>
      {children}
    </StandardROIContext.Provider>
  )
}

export function useStandardROI() {
  const context = useContext(StandardROIContext)
  if (context === undefined) {
    throw new Error('useStandardROI must be used within a StandardROIProvider')
  }
  return context
}